import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useLocation, useSearchParams } from 'react-router-dom';
import { athleteAPI, trainingSessionAPI, bananaCupAPI } from '../../services/api';
import { Athlete } from '../../types/mongodb';
import { getQuickDateRange } from './dateRangeUtils';
import { loadAnalyticsFilters, saveAnalyticsFilters, getPagePathFromRoute } from '../../utils/analyticsFilterStorage';
import {
  AnalyticsContainer,
  PageHeader,
  PageTitle,
  PageDescription,
  DataSelectionSection,
  SelectionGrid,
  SelectionGroup,
  GraphSection,
  GraphSectionWithPeriodSelector,
  GraphTitle,
  GraphContainer,
  EmptyState,
  PeriodSelectorTopRight,
  PeriodButtonWrapperSmall,
  PeriodButtonSmall,
  PeriodLabelSmall,
} from './styles';
import {
  DateRangeDropdown,
  AthletesDropdown,
} from './FilterDropdowns';
import { getAthleteColor } from '../../utils/athleteColors';
import styled from 'styled-components';

const ListSection = styled.section`
  background: var(--bg-primary);
  border-radius: var(--radius-md);
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid var(--card-border);
  box-shadow: var(--shadow-sm);
`;

const ListTitle = styled.h3`
  font-size: 1.3rem;
  color: var(--accent-orange);
  font-family: 'Poppins', sans-serif;
  font-weight: 800;
  margin-bottom: 1.5rem;
  text-align: left;
`;


const ListTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  
  @media (max-width: 768px) {
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    white-space: nowrap;
  }
`;

const ListTableHeader = styled.th`
  text-align: left;
  padding: 0.75rem;
  color: var(--text-gray);
  font-size: 0.85rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 800;
  border-bottom: 2px solid rgba(0, 0, 0, 0.1);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
    padding: 0.5rem 0.3rem;
  }
`;

const ListTableCell = styled.td`
  padding: 0.75rem;
  color: var(--text-dark);
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  font-weight: 500;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
    padding: 0.5rem 0.3rem;
  }
`;

const ListTableRow = styled.tr`
  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }
`;


interface TrainingSession {
  _id: string;
  date: string;
  trainingType: string;
  jumps?: Array<{
    athleteId: string | { _id: string; firstName: string; lastName: string };
    rating?: number;
  }>;
  competitionJumps?: Array<{
    athleteId: string | { _id: string; firstName: string; lastName: string };
    rating?: number;
  }>;
  groundTrainings?: Array<any>;
  crossCountryTrainings?: Array<any>;
}

interface RatingData {
  date: string;
  rating: number;
}

interface RatingFormAnalyticsProps {
  isJumperMode?: boolean;
}

const RatingFormAnalytics: React.FC<RatingFormAnalyticsProps> = ({ isJumperMode = false }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const pagePath = getPagePathFromRoute(location.pathname, searchParams);
  
  // Load saved filters on mount
  const savedFilters = loadAnalyticsFilters(pagePath);
  
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [sessions, setSessions] = useState<TrainingSession[]>([]);
  const [bananaCups, setBananaCups] = useState<any[]>([]);
  const [selectedAthletes, setSelectedAthletes] = useState<string[]>(savedFilters?.selectedAthletes || []);
  const [startDate, setStartDate] = useState<string>(savedFilters?.startDate || '');
  const [endDate, setEndDate] = useState<string>(savedFilters?.endDate || '');
  const [selectedPeriod, setSelectedPeriod] = useState<'daily' | 'weekly' | 'monthly'>(savedFilters?.selectedPeriod || 'daily');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const isInitialMount = useRef(true);

  // Data states for graphs
  const [dailyRatingData, setDailyRatingData] = useState<any[]>([]);
  const [weeklyRatingData, setWeeklyRatingData] = useState<any[]>([]);
  const [monthlyRatingData, setMonthlyRatingData] = useState<any[]>([]);
  
  // Data states for lists
  const [lastMonthRatings, setLastMonthRatings] = useState<Array<{ athlete: string; avgRating: number; count: number }>>([]);
  const [last6MonthsRatings, setLast6MonthsRatings] = useState<Array<{ athlete: string; avgRating: number; count: number }>>([]);
  const [bestMonthRatings, setBestMonthRatings] = useState<Array<{ athlete: string; month: string; avgRating: number }>>([]);
  const [jumperAthleteId, setJumperAthleteId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(typeof window !== 'undefined' && window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // If jumper mode, get the logged-in jumper's athlete ID first
        if (isJumperMode) {
          try {
            const myAthlete = await athleteAPI.getMyAthlete();
            const athleteId = myAthlete._id.toString();
            setJumperAthleteId(athleteId);
            setSelectedAthletes([athleteId]);
          } catch (error) {
            console.error('Error loading jumper athlete:', error);
          }
        }
        
        const [athletesData, sessionsResponse, bananaCupsData] = await Promise.all([
          athleteAPI.getAll(),
          trainingSessionAPI.getAll(undefined, undefined, true), // Load all sessions for analytics
          bananaCupAPI.getAll(),
        ]);

        // Handle response - when all=true, returns array directly, otherwise paginated object
        const sessionsData = Array.isArray(sessionsResponse) ? sessionsResponse : (sessionsResponse.sessions || []);

        setAthletes(athletesData);
        setSessions(Array.isArray(sessionsData) ? sessionsData : []);
        setBananaCups(bananaCupsData || []);

        // Set default date range (last 6 months) only if no saved filters exist
        if (!savedFilters || (!savedFilters.startDate && !savedFilters.endDate)) {
          const today = new Date();
          const sixMonthsAgo = new Date();
          sixMonthsAgo.setMonth(today.getMonth() - 6);
          setEndDate(today.toISOString().split('T')[0]);
          setStartDate(sixMonthsAgo.toISOString().split('T')[0]);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [isJumperMode]);

  // Save filters whenever they change (skip on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    saveAnalyticsFilters(pagePath, {
      selectedAthletes,
      startDate,
      endDate,
      selectedPeriod,
    });
  }, [pagePath, selectedAthletes, startDate, endDate, selectedPeriod]);

  // Helper function to format date for input
  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    if (dateString.includes('-') && dateString.length === 10) {
      return dateString;
    }
    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateString;
  };

  // Helper function to check if date is in range
  const isDateInRange = (dateString: string, start: string, end: string): boolean => {
    if (!start || !end) return true;
    return dateString >= start && dateString <= end;
  };

  // Process rating data
  useEffect(() => {
    if (!sessions.length || !startDate || !endDate || selectedAthletes.length === 0) {
      setDailyRatingData([]);
      setWeeklyRatingData([]);
      setMonthlyRatingData([]);
      setLastMonthRatings([]);
      setLast6MonthsRatings([]);
      setBestMonthRatings([]);
      return;
    }

    // Helper function to check if a session has been recorded
    const isRecordedSession = (session: TrainingSession): boolean => {
      const hasJumps = !!(session.jumps && session.jumps.length > 0);
      const hasCompetitionJumps = !!(session.competitionJumps && session.competitionJumps.length > 0);
      return hasJumps || hasCompetitionJumps;
    };

    // Filter sessions by date range
    const filteredSessions = sessions.filter(session => {
      if (!isDateInRange(session.date, startDate, endDate)) return false;
      if (!isRecordedSession(session)) return false;
      return true;
    });

    // Collect ratings per athlete and date
    const athleteRatingMap = new Map<string, Map<string, number[]>>(); // athleteId -> date -> ratings[]

    filteredSessions.forEach(session => {
      // Process regular jumps
      if (session.jumps) {
        session.jumps.forEach(jump => {
          const athleteIdRaw = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
          const athleteId = String(athleteIdRaw);
          
          if (!selectedAthletes.includes(athleteId)) return;
          if (!jump.rating || jump.rating <= 0) return;

          if (!athleteRatingMap.has(athleteId)) {
            athleteRatingMap.set(athleteId, new Map());
          }
          const dateMap = athleteRatingMap.get(athleteId)!;
          if (!dateMap.has(session.date)) {
            dateMap.set(session.date, []);
          }
          dateMap.get(session.date)!.push(jump.rating);
        });
      }

      // Process competition jumps
      if (session.competitionJumps) {
        session.competitionJumps.forEach(jump => {
          const athleteIdRaw = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
          const athleteId = String(athleteIdRaw);
          
          if (!selectedAthletes.includes(athleteId)) return;
          if (!jump.rating || jump.rating <= 0) return;

          if (!athleteRatingMap.has(athleteId)) {
            athleteRatingMap.set(athleteId, new Map());
          }
          const dateMap = athleteRatingMap.get(athleteId)!;
          if (!dateMap.has(session.date)) {
            dateMap.set(session.date, []);
          }
          dateMap.get(session.date)!.push(jump.rating);
        });
      }
    });

    // Process banana cup jumps
    bananaCups.forEach(bananaCup => {
      if (!bananaCup.competitions || !Array.isArray(bananaCup.competitions)) return;
      
      bananaCup.competitions.forEach((competition: any) => {
        if (!competition.isCompleted || !competition.jumps || !Array.isArray(competition.jumps)) return;
        if (!isDateInRange(competition.date, startDate, endDate)) return;
        
        competition.jumps.forEach((jump: any) => {
          const athleteIdRaw = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
          const athleteId = String(athleteIdRaw);
          
          if (!selectedAthletes.includes(athleteId)) return;

          // Check all rounds for ratings
          const ratings = [
            jump.firstRoundRating,
            jump.secondRoundRating,
            jump.thirdRoundRating,
            jump.fourthRoundRating,
            jump.fifthRoundRating,
          ].filter((r): r is number => r !== undefined && r !== null && r > 0);

          if (ratings.length > 0) {
            if (!athleteRatingMap.has(athleteId)) {
              athleteRatingMap.set(athleteId, new Map());
            }
            const dateMap = athleteRatingMap.get(athleteId)!;
            if (!dateMap.has(competition.date)) {
              dateMap.set(competition.date, []);
            }
            ratings.forEach(rating => dateMap.get(competition.date)!.push(rating));
          }
        });
      });
    });

    // Generate date ranges
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Daily data
    const dailyData: any[] = [];
    const current = new Date(start);
    while (current <= end) {
      const dateStr = current.toISOString().split('T')[0];
      const point: any = { date: dateStr };
      
      selectedAthletes.forEach(athleteId => {
        const athlete = athletes.find(a => a._id === athleteId);
        if (!athlete) return;
        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
        const dateMap = athleteRatingMap.get(athleteId);
        
        if (dateMap && dateMap.has(dateStr)) {
          const ratings = dateMap.get(dateStr)!;
          const avg = ratings.reduce((a, b) => a + b, 0) / ratings.length;
          point[athleteName] = Math.round(avg * 10) / 10;
        } else {
          point[athleteName] = null;
        }
      });
      
      dailyData.push(point);
      current.setDate(current.getDate() + 1);
    }

    // Weekly data
    const weeklyData: any[] = [];
    const weekStart = new Date(start);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1); // Start of week (Monday)
    
    while (weekStart <= end) {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      const weekKey = `${weekStart.toISOString().split('T')[0]}`;
      const point: any = { week: weekKey };
      
      selectedAthletes.forEach(athleteId => {
        const athlete = athletes.find(a => a._id === athleteId);
        if (!athlete) return;
        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
        const dateMap = athleteRatingMap.get(athleteId);
        
        const weekRatings: number[] = [];
        const checkDate = new Date(weekStart);
        while (checkDate <= weekEnd && checkDate <= end) {
          const dateStr = checkDate.toISOString().split('T')[0];
          if (dateMap && dateMap.has(dateStr)) {
            weekRatings.push(...dateMap.get(dateStr)!);
          }
          checkDate.setDate(checkDate.getDate() + 1);
        }
        
        if (weekRatings.length > 0) {
          const avg = weekRatings.reduce((a, b) => a + b, 0) / weekRatings.length;
          point[athleteName] = Math.round(avg * 10) / 10;
        } else {
          point[athleteName] = null;
        }
      });
      
      weeklyData.push(point);
      weekStart.setDate(weekStart.getDate() + 7);
    }

    // Monthly data
    const monthlyData: any[] = [];
    const monthStart = new Date(start.getFullYear(), start.getMonth(), 1);
    
    while (monthStart <= end) {
      const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
      const monthKey = `${monthStart.getFullYear()}-${String(monthStart.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = monthStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      const point: any = { month: monthLabel };
      
      selectedAthletes.forEach(athleteId => {
        const athlete = athletes.find(a => a._id === athleteId);
        if (!athlete) return;
        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
        const dateMap = athleteRatingMap.get(athleteId);
        
        const monthRatings: number[] = [];
        const checkDate = new Date(monthStart);
        while (checkDate <= monthEnd && checkDate <= end) {
          const dateStr = checkDate.toISOString().split('T')[0];
          if (dateMap && dateMap.has(dateStr)) {
            monthRatings.push(...dateMap.get(dateStr)!);
          }
          checkDate.setDate(checkDate.getDate() + 1);
        }
        
        if (monthRatings.length > 0) {
          const avg = monthRatings.reduce((a, b) => a + b, 0) / monthRatings.length;
          point[athleteName] = Math.round(avg * 10) / 10;
        } else {
          point[athleteName] = null;
        }
      });
      
      monthlyData.push(point);
      monthStart.setMonth(monthStart.getMonth() + 1);
    }

    // Calculate lists
    const now = new Date();
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
    const lastMonthStartStr = lastMonthStart.toISOString().split('T')[0];
    const lastMonthEndStr = lastMonthEnd.toISOString().split('T')[0];

    const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, 1);
    const sixMonthsAgoStr = sixMonthsAgo.toISOString().split('T')[0];

    const lastMonthList: Array<{ athlete: string; avgRating: number; count: number }> = [];
    const last6MonthsList: Array<{ athlete: string; avgRating: number; count: number }> = [];
    const bestMonthList: Array<{ athlete: string; month: string; avgRating: number }> = [];

    selectedAthletes.forEach(athleteId => {
      const athlete = athletes.find(a => a._id === athleteId);
      if (!athlete) return;
      const athleteName = `${athlete.firstName} ${athlete.lastName}`;
      const dateMap = athleteRatingMap.get(athleteId);
      
      if (!dateMap) return;

      // Last month
      const lastMonthRatings: number[] = [];
      const checkDate = new Date(lastMonthStart);
      while (checkDate <= lastMonthEnd) {
        const dateStr = checkDate.toISOString().split('T')[0];
        if (dateMap.has(dateStr)) {
          lastMonthRatings.push(...dateMap.get(dateStr)!);
        }
        checkDate.setDate(checkDate.getDate() + 1);
      }
      if (lastMonthRatings.length > 0) {
        const avg = lastMonthRatings.reduce((a, b) => a + b, 0) / lastMonthRatings.length;
        lastMonthList.push({
          athlete: athleteName,
          avgRating: Math.round(avg * 10) / 10,
          count: lastMonthRatings.length,
        });
      }

      // Last 6 months
      const last6MonthsRatings: number[] = [];
      const checkDate6 = new Date(sixMonthsAgo);
      while (checkDate6 <= now) {
        const dateStr = checkDate6.toISOString().split('T')[0];
        if (dateMap.has(dateStr)) {
          last6MonthsRatings.push(...dateMap.get(dateStr)!);
        }
        checkDate6.setDate(checkDate6.getDate() + 1);
      }
      if (last6MonthsRatings.length > 0) {
        const avg = last6MonthsRatings.reduce((a, b) => a + b, 0) / last6MonthsRatings.length;
        last6MonthsList.push({
          athlete: athleteName,
          avgRating: Math.round(avg * 10) / 10,
          count: last6MonthsRatings.length,
        });
      }

      // Best month
      const monthlyAverages: Array<{ month: string; avg: number }> = [];
      const monthStartCheck = new Date(sixMonthsAgo);
      while (monthStartCheck <= now) {
        const monthEndCheck = new Date(monthStartCheck.getFullYear(), monthStartCheck.getMonth() + 1, 0);
        const monthRatings: number[] = [];
        const checkDateMonth = new Date(monthStartCheck);
        while (checkDateMonth <= monthEndCheck && checkDateMonth <= now) {
          const dateStr = checkDateMonth.toISOString().split('T')[0];
          if (dateMap.has(dateStr)) {
            monthRatings.push(...dateMap.get(dateStr)!);
          }
          checkDateMonth.setDate(checkDateMonth.getDate() + 1);
        }
        if (monthRatings.length > 0) {
          const avg = monthRatings.reduce((a, b) => a + b, 0) / monthRatings.length;
          monthlyAverages.push({
            month: monthStartCheck.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
            avg,
          });
        }
        monthStartCheck.setMonth(monthStartCheck.getMonth() + 1);
      }
      if (monthlyAverages.length > 0) {
        const bestMonth = monthlyAverages.reduce((best, current) => 
          current.avg > best.avg ? current : best
        );
        bestMonthList.push({
          athlete: athleteName,
          month: bestMonth.month,
          avgRating: Math.round(bestMonth.avg * 10) / 10,
        });
      }
    });

    // Sort lists
    lastMonthList.sort((a, b) => b.avgRating - a.avgRating);
    last6MonthsList.sort((a, b) => b.avgRating - a.avgRating);
    bestMonthList.sort((a, b) => b.avgRating - a.avgRating);

    setDailyRatingData(dailyData);
    setWeeklyRatingData(weeklyData);
    setMonthlyRatingData(monthlyData);
    setLastMonthRatings(lastMonthList);
    setLast6MonthsRatings(last6MonthsList);
    setBestMonthRatings(bestMonthList);
  }, [sessions, startDate, endDate, selectedAthletes, athletes, bananaCups]);

  const handleAthleteToggle = (athleteId: string) => {
    setSelectedAthletes(prev =>
      prev.includes(athleteId)
        ? prev.filter(id => id !== athleteId)
        : [...prev, athleteId]
    );
  };

  const handleUpdateDate = () => {
    // Data will be re-processed automatically via useEffect
  };

  const handleQuickDateRange = (range: string) => {
    const { startDate: newStartDate, endDate: newEndDate } = getQuickDateRange(range);
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleAthletesSave = (selected: string[]) => {
    setSelectedAthletes(selected);
  };


  if (isLoading) {
    return (
      <AnalyticsContainer>
        <EmptyState>Loading rating/form data...</EmptyState>
      </AnalyticsContainer>
    );
  }

  return (
    <AnalyticsContainer>
      <PageHeader>
        <PageTitle>Rating / Form Analytics</PageTitle>
        <PageDescription>
          Track and analyze athlete ratings and form over time to monitor performance trends.
        </PageDescription>
      </PageHeader>

      {/* Data Selection Section */}
      <DataSelectionSection>
        <SelectionGrid>
          <SelectionGroup>
            <DateRangeDropdown
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onQuickDateRange={handleQuickDateRange}
              onUpdate={handleUpdateDate}
              formatDateForInput={formatDateForInput}
            />
          </SelectionGroup>

          {!isJumperMode && (
            <SelectionGroup>
              <AthletesDropdown
                athletes={athletes}
                selectedAthletes={selectedAthletes}
                onAthleteToggle={handleAthleteToggle}
                onSave={handleAthletesSave}
              />
            </SelectionGroup>
          )}
        </SelectionGrid>
      </DataSelectionSection>

      {/* Graphs Section */}
      
      {/* Combined Rating Line Chart with Period Selector */}
      <GraphSectionWithPeriodSelector>
        <GraphTitle>Rating / Form</GraphTitle>
        {(() => {
          const hasDailyData = dailyRatingData.length > 0 && dailyRatingData.some(d => Object.keys(d).some(k => k !== 'date' && d[k] !== null));
          const hasWeeklyData = weeklyRatingData.length > 0 && weeklyRatingData.some(d => Object.keys(d).some(k => k !== 'week' && d[k] !== null));
          const hasMonthlyData = monthlyRatingData.length > 0 && monthlyRatingData.some(d => Object.keys(d).some(k => k !== 'month' && d[k] !== null));
          const hasAnyData = hasDailyData || hasWeeklyData || hasMonthlyData;
          
          return hasAnyData ? (
            <PeriodSelectorTopRight>
              <PeriodButtonWrapperSmall>
                <PeriodButtonSmall
                  $isSelected={selectedPeriod === 'daily'}
                  onClick={() => setSelectedPeriod('daily')}
                  title="Daily"
                >
                  D
                </PeriodButtonSmall>
                <PeriodLabelSmall>Daily</PeriodLabelSmall>
              </PeriodButtonWrapperSmall>
              <PeriodButtonWrapperSmall>
                <PeriodButtonSmall
                  $isSelected={selectedPeriod === 'weekly'}
                  onClick={() => setSelectedPeriod('weekly')}
                  title="Weekly"
                >
                  W
                </PeriodButtonSmall>
                <PeriodLabelSmall>Weekly</PeriodLabelSmall>
              </PeriodButtonWrapperSmall>
              <PeriodButtonWrapperSmall>
                <PeriodButtonSmall
                  $isSelected={selectedPeriod === 'monthly'}
                  onClick={() => setSelectedPeriod('monthly')}
                  title="Monthly"
                >
                  M
                </PeriodButtonSmall>
                <PeriodLabelSmall>Monthly</PeriodLabelSmall>
              </PeriodButtonWrapperSmall>
            </PeriodSelectorTopRight>
          ) : null;
        })()}
        {startDate && endDate && selectedAthletes.length > 0 ? (
          (() => {
            const currentData = selectedPeriod === 'daily' ? dailyRatingData : selectedPeriod === 'weekly' ? weeklyRatingData : monthlyRatingData;
            const dataKey = selectedPeriod === 'daily' ? 'date' : selectedPeriod === 'weekly' ? 'week' : 'month';
            const hasData = currentData.length > 0;
            
            return hasData ? (
              <GraphContainer>
                <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                  <LineChart data={currentData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey={dataKey} 
                      style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                      tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                      tickFormatter={(value) => {
                        if (selectedPeriod === 'monthly') return value;
                        try {
                          const date = new Date(value);
                          if (isNaN(date.getTime())) return value;
                          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        } catch {
                          return value;
                        }
                      }}
                    />
                    <YAxis 
                      domain={[0, 10]}
                      style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                      tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                    />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontFamily: 'Poppins', fontSize: isMobile ? '0.5rem' : '0.75rem' }} iconSize={isMobile ? 8 : 12} />
                    {athletes
                      .filter(a => selectedAthletes.includes(a._id))
                      .map((athlete, index) => {
                        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
                        return (
                          <Line
                            key={athlete._id}
                            type="monotone"
                            dataKey={athleteName}
                            stroke={getAthleteColor(athlete, index)}
                            strokeWidth={2}
                            name={athleteName}
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                            connectNulls={true}
                          />
                        );
                      })}
                  </LineChart>
                </ResponsiveContainer>
              </GraphContainer>
            ) : (
              <EmptyState>
                No rating data available for the selected period and filters
              </EmptyState>
            );
          })()
        ) : (
          <EmptyState>
            {!startDate || !endDate 
              ? 'Please select a date range'
              : selectedAthletes.length === 0
              ? 'Please select at least one athlete'
              : 'No rating data available for the selected filters'}
          </EmptyState>
        )}
      </GraphSectionWithPeriodSelector>
    </AnalyticsContainer>
  );
};

export default RatingFormAnalytics;

